/**
 * Shared celebrant configuration used by Quick Create, the multi-event form,
 * and the AI chat creation flow so role suggestions and minimum-count rules
 * never drift between surfaces.
 */

export interface CelebrantEntry {
  name: string;
  role: string;
  email: string;
}

// Predefined celebrant roles per celebration type. These are suggestions only
// (surfaced as chips/datalist options) — any free-text role is always accepted,
// so same-sex and non-traditional pairings are never blocked from a matching label.
export const CELEBRANT_ROLES_BY_TYPE: Record<string, string[]> = {
  wedding:            ['Bride', 'Groom', 'Partner'],
  engagement:         ['Bride-to-Be', 'Groom-to-Be', 'Partner'],
  anniversary:        ['Spouse', 'Partner'],
  birthday:           ['Birthday Person'],
  baby_shower:        ['Mom-to-Be', 'Dad-to-Be', 'Parent-to-Be'],
  graduation:         ['Graduate'],
  religious_ceremony: ['Honoree', 'Child', 'Devotee'],
  coming_of_age:      ['Honoree'],
  naming_ceremony:    ['Child', 'Honoree'],
  memorial:           ['Honoree'],
  retirement:         ['Retiree'],
  housewarming:       ['Host'],
  reunion:            ['Host', 'Honoree'],
  fundraiser:         ['Host', 'Honoree'],
  festival:           ['Host', 'Honoree'],
  other:              ['Honoree', 'Celebrant'],
};

// Minimum number of celebrants required per celebration type.
// Couple-centric celebrations need two celebrants; everything else needs one.
// Any type can still add more celebrants beyond the minimum (e.g. twins' birthday).
export const MIN_CELEBRANTS_BY_TYPE: Record<string, number> = {
  wedding: 2,
  engagement: 2,
  anniversary: 2,
};

export const getMinCelebrantsForType = (celebrationType?: string): number => {
  const normalized = (celebrationType ?? '').toLowerCase();
  for (const [type, minimum] of Object.entries(MIN_CELEBRANTS_BY_TYPE)) {
    if (normalized.includes(type)) {
      return minimum;
    }
  }
  return 1;
};

export const getCelebrantRoleOptions = (celebrationType?: string): string[] => {
  const normalized = (celebrationType ?? '').toLowerCase();
  for (const [type, roles] of Object.entries(CELEBRANT_ROLES_BY_TYPE)) {
    if (type !== 'other' && normalized.includes(type)) {
      return roles;
    }
  }
  return CELEBRANT_ROLES_BY_TYPE.other;
};

/** Predicted role label for the celebrant at `index` (0-based) of a given type. */
export const getPredictedRoleAt = (celebrationType: string | undefined, index: number): string => {
  const options = getCelebrantRoleOptions(celebrationType);
  return options[index] ?? options[options.length - 1] ?? 'Celebrant';
};

/**
 * Pads a celebrants array up to the type's minimum count, prefilling roles
 * from `CELEBRANT_ROLES_BY_TYPE`. Never removes existing entries.
 */
export const ensureMinCelebrants = (
  celebrants: CelebrantEntry[],
  celebrationType: string | undefined,
): CelebrantEntry[] => {
  const minCount = getMinCelebrantsForType(celebrationType);
  if (celebrants.length >= minCount) {
    return celebrants;
  }
  const next = [...celebrants];
  while (next.length < minCount) {
    next.push({ name: '', role: getPredictedRoleAt(celebrationType, next.length), email: '' });
  }
  return next;
};

/**
 * Converts a celebrants array into the parallel `celebrant_names`/`celebrant_roles`/
 * `celebrant_emails` arrays expected by the create-celebration API, trimming values
 * and dropping trailing rows the user added but left completely blank.
 */
export const toCelebrantArrays = (
  celebrants: CelebrantEntry[],
): { names: string[]; roles: string[]; emails: string[] } => {
  const trimmed = celebrants.map((c) => ({
    name: c.name.trim(),
    role: c.role.trim(),
    email: c.email.trim(),
  }));

  while (trimmed.length > 1 && !trimmed[trimmed.length - 1].name && !trimmed[trimmed.length - 1].email) {
    trimmed.pop();
  }

  return {
    names: trimmed.map((c) => c.name),
    roles: trimmed.map((c) => c.role || 'Celebrant'),
    emails: trimmed.map((c) => c.email),
  };
};
